import React, { useState } from "react";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-solid.css";
import { FontStyles } from "../../utils/font-styles.ts";
import { NumericInput } from "../../components/inputs/numeric-input.tsx";
import { DropdownItem, Select } from "../../components/inputs/select-dropdown.tsx";
import { InputIcon } from "../../components/input-icon.tsx";
import { useReportCalculatorInteraction } from "../hooks/use-report-calculator-interaction.ts";
interface LBTTCalculatorProps {
  price: number;
}

const options: DropdownItem[] = [
  { value: "1", name: "My next home" },
  { value: "2", name: "My first home" },
  { value: "3", name: "A second or additional home" },
];

export const LBTTCalculator: React.FC<LBTTCalculatorProps> = ({ price }) => {
  const [propertyPrice, setPropertyPrice] = useState<number>(price);
  const [transactionType, setTransactionType] = useState<number>(1);

  const reportFirstInteraction = useReportCalculatorInteraction("lbtt");

  let result = 0;
  const nilRate = transactionType === 2 ? 175000 : 145000;
  if (propertyPrice && propertyPrice > nilRate) {
    const bands = [
      { lowerLimit: 0, limit: nilRate, rate: transactionType === 3 ? 0.08 : 0 },
      { lowerLimit: nilRate, limit: 250000, rate: transactionType === 3 ? 0.1 : 0.02 },
      { lowerLimit: 250000, limit: 325000, rate: transactionType === 3 ? 0.13 : 0.05 },
      { lowerLimit: 325000, limit: 750000, rate: transactionType === 3 ? 0.18 : 0.1 },
      { lowerLimit: 750000, limit: Infinity, rate: transactionType === 3 ? 0.2 : 0.12 },
    ];
    for (const { lowerLimit, limit, rate } of bands) {
      if (propertyPrice > lowerLimit) {
        const taxableAmount = Math.min(propertyPrice, limit) - lowerLimit;
        result += taxableAmount * rate;
      }
    }
  }

  return (
    <div className={"flex flex-col gap-4 pt-4 md:gap-6 md:pt-6"}>
      <div
        className={`${FontStyles["body-md"]} bg-cloud text-slate p-3.5 md:py-4 md:px-5.5 border-neutral-200 border-2 rounded-md`}
      >
        Land and Buildings Transaction Tax (LBTT) is a tax applied to the purchase of property or land in Scotland. Use
        our calculator below to find out how much LBTT you will pay on a residential purchase (there is a different LBTT
        rate for non-residential purchases). First time buyers get some LBTT relief. These estimates are provided as
        illustrative figures for guidance only. Consider seeking advice from a professional mortgage advisor.
      </div>
      <div className={"flex flex-col md:flex-row gap-3.5 md:gap-6"}>
        <Select
          label={"I am buying..."}
          labelSize="large"
          options={options}
          onValueChange={(item) => {
            reportFirstInteraction();
            setTransactionType(Number(item));
          }}
          value={transactionType.toString()}
        />
        <NumericInput
          state={"default"}
          value={propertyPrice}
          renderLeadingIcon={() => <InputIcon icon={`fa-solid fa-sharp fa-sterling-sign`} />}
          label={"Property price"}
          labelSize="large"
          placeholder={"Enter property price"}
          onValueChange={(value) => {
            reportFirstInteraction();
            setPropertyPrice(value);
          }}
        />
      </div>
      <div
        className={
          `${FontStyles["body-l"]} leading-snug md:leading-snug text-neutral-black p-3 bg-loch-50 ` +
          `border border-loch rounded-md text-center`
        }
      >
        Your total LBTT tax would be{" "}
        <span className={"font-semibold text-small-h5 md:text-h3"}>£{Math.round(result).toLocaleString("en-GB")}</span>
      </div>
    </div>
  );
};
